<template>
    <div>
        <v-progress-linear
            v-if="isLoading || isSorting"
            indeterminate
            color="primary"
            class="mb-5">
        </v-progress-linear>

        <div v-if="!isLoading && !formIsOpen">
            <v-card-title>
                <slot name="title">
                    Packing Rules
                </slot>
                <v-spacer></v-spacer>
                <TestPackingRules v-if="isAdmin" :integrationId="id" />

                <v-btn
                    v-if="isAdmin && packingRules && packingRules.length > 0"
                    class="ml-2"
                    small
                    @click="shareBoxSettings">
                    {{ shareBtnText }}
                </v-btn>

                <v-btn small class="ml-2" color="primary" @click="openPackingRuleForm(0, false)">
                    Add Packing Rule
                </v-btn>
            </v-card-title>

            <div v-if="packingRules && packingRules.length === 0" class="no-data">
                No packing rules found. Create new.
            </div>

            <div v-else>
                <div v-if="selectedPackingRules.length > 0" class="mb-3">
                    <v-btn
                         x-small
                         class="mr-2 white--text"
                         color="#FF7033" @click="confirmDelete(selectedPackingRules)"
                        >
                        Delete Selected
                    </v-btn>

                    <v-btn
                        x-small
                        class="mr-2"
                        @click="duplicatePackingRules(selectedPackingRules)"
                        :disabled="isDuplicating"
                        >
                        Duplicate Selected
                    </v-btn>

                    <v-btn
                        v-if="isAdmin"
                        x-small
                        @click="confirmCopy(selectedPackingRules)"
                        >
                        Copy Selected
                    </v-btn>
                </div>

                <table  class="table table-hover" :class="{ 'disabled-table': isSorting }">
                    <thead>
                        <tr>
                            <th width="30"></th>
                            <th width="30">
                                <v-checkbox
                                    class="ma-0 pa-0"
                                    hide-details
                                    dense
                                    v-model="selectAll"
                                    @change="toggleAll"
                                    >
                                </v-checkbox>
                            </th>
                            <th width="60">Order</th>
                            <th>Label</th>
                            <th>Target</th>
                            <th>Packing Method</th>
                            <th>Status</th>
                            <th width="270">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(packingRule, index) in packingRules"
                            :key="packingRule.index"
                            :draggable="!isSaving"
                            @dragstart="dragStart(index)" @dragover.prevent
                            @drop="drop(index)">
                            <td>
                                <!-- Only show drag icon if not the last row -->
                                <v-icon class="drag-handle" color="grey darken-1">
                                    mdi-drag
                                </v-icon>
                            </td>
                            <td>
                                <v-checkbox
                                    :value="index"
                                    v-model="selectedPackingRules"
                                    class="ma-0 pa-0"
                                    hide-details
                                    dense
                                    >
                                </v-checkbox>
                            </td>
                            <td>
                                {{ index + 1 }}
                            </td>
                            <td>{{ packingRule.label }}</td>
                            <td>{{ targetLabel[packingRule.target] }}</td>
                            <td>{{ getPackingMethodByKey(packingRule.packing_method) }}</td>
                            <td>
                                <v-switch
                                    class="switch"
                                    :disabled="isSaving"
                                    v-model="packingRule.enabled"
                                    @change="switchPackingRule(index)"
                                    :label="packingRule.enabled ? 'Enabled' : 'Disabled'"
                                    hide-details
                                    :loading="currentSwitchingIndex == index && isSaving"
                                    dense>
                                </v-switch>
                            </td>
                            <td>
                                <v-btn x-small @click="duplicatePackingRules([index])" :disabled="isDuplicating">Duplicate</v-btn>
                                <v-btn v-if="isAdmin" x-small @click="confirmCopy([index])">Copy</v-btn>
                                <v-btn x-small @click="openPackingRuleForm(index, true)">Edit</v-btn>
                                <v-btn x-small class="white--text" color="#FF7033" @click="confirmDelete(index)">Delete</v-btn>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Delete Confirmation Dialog -->
            <v-dialog v-model="isDeleteDialogOpen" max-width="400px">
                <v-card>
                    <v-card-title>Confirm Delete</v-card-title>
                    <v-card-text>Are you sure you want to delete packing rule?</v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="blue darken-1" text @click="isDeleteDialogOpen = false; deleteIndex=null">Cancel</v-btn>
                        <v-btn color="#FF7033" text @click="deletePackingRule">
                            <span v-if="isSaving">
                                <v-progress-circular
                                    indeterminate
                                    size="12"
                                    width="2"
                                    color="primary darken-3">
                                </v-progress-circular>
                                Deleting...
                            </span>
                            <span v-else>Delete</span>
                        </v-btn>

                    </v-card-actions>
                </v-card>
            </v-dialog>

            <v-dialog v-model="isCopyDialogOpen" max-width="400px">
                <v-card>
                    <v-card-title>Copy Packing Rule{{ selectedPackingRules.length > 1 ? 's' : '' }} To</v-card-title>
                    <v-card-text>
                        <div>Please enter the Integration Id to copy the selected packing rule{{ selectedPackingRules.length > 1 ? 's' : '' }} to:</div>
                        <v-text-field type="number" v-model="copyDestination" label="Integration ID" />
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="blue darken-1" text @click="isCopyDialogOpen = false;">Cancel</v-btn>
                        <v-btn
                            color="primary"
                            text
                            @click="copyPackingRules()"
                            :disabled="!copyDestination"
                            >
                            <span v-if="isSaving">
                                <v-progress-circular
                                    indeterminate
                                    size="12"
                                    width="2"
                                    color="primary darken-3">
                                </v-progress-circular>
                                Copying...
                            </span>
                            <span v-else>Copy</span>
                        </v-btn>

                    </v-card-actions>
                </v-card>
            </v-dialog>
        </div>

        <v-row v-if="formIsOpen" class="mx-0">
            <v-col class="px-0">
                <v-progress-linear
                    v-if="loadingPackages"
                    indeterminate
                    color="primary">
                </v-progress-linear>
                <PackingRuleForm
                    v-else
                    @closePackingRuleForm="formIsOpen=false"
                    :integrationId="id"
                    :packingRuleIndex="selectedPackingRuleIndex"
                    :packingRules="packingRules"
                    :isEdit="isEdit"
                />
            </v-col>
        </v-row>

    </div>
</template>

<script>
import { EventBus } from '@/js/event-bus.js'
import PackingRuleForm from './PackingRuleForm.vue';
import { PACKING_METHODS, PACKING_METHODS_WITH_BOXES } from '@/js/constants/packing-rules';
import TestPackingRules from './TestPackingRules.vue';
import { mapGetters } from 'vuex'

export default {
    components: {
        PackingRuleForm,
        TestPackingRules
    },

    props: {
        id: { type: Number, default: null }
    },

    data() {
        return {
            isLoading: true,
            isSaving: false,
            isSorting: false,

            draggedItemIndex: null,

            packingRules: null,

            isDeleteDialogOpen: false,
            deleteIndex: null,

            selectedPackingRuleIndex: 0,
            formIsOpen: false,
            isEdit: true,

            targetLabel: {
                'unpacked': 'Unpacked Items',
                'all': 'All Items',
            },

            shareBtnText: "Share Packing Rules",
            originalShareBtnText: null,
            packingMethods: PACKING_METHODS,
            currentSwitchingIndex: null,
            loadingPackages: false,

            selectAll: false,
            selectedPackingRules: [],

            isCopyDialogOpen: false,
            copyDestination: null,
            copyPackingRuleIds: [],
            isDuplicating: false
        };
    },

    computed: {
        isAdmin() {
            return this.$store.getters.isAdmin
        },

        ...mapGetters('LRSettingsState', {
            shopifyUserId: 'getShopifyUserId'
        }),
    },

    watch: {
        selectedPackingRules(val) {
            // Update "Select All" checkbox based on selection
            this.selectAll = this.packingRules.length > 0 && val.length === this.packingRules.length;
        }
    },

    mounted() {

        this.originalShareBtnText = this.shareBtnText;

        this.$http
            .get(`/api/packing-rules/${this.id}`)
            .then(({ data }) => {
                this.packingRules = data.data ?? [];
            })
            .catch(err => {
                EventBus.$emit('show-error', err)
            })
            .finally(() => {
                this.isLoading = false
            })
    },

    methods: {
        dragStart(index) {
            this.draggedItemIndex = index;
        },
        drop(index) {

            // skip if not changed
            if(index === this.draggedItemIndex) {
                return;
            }

            if (this.draggedItemIndex !== null) {
                const draggedItem = this.packingRules[this.draggedItemIndex];
                draggedItem.order = index + 1;

                this.packingRules.splice(this.draggedItemIndex, 1);
                this.packingRules.splice(index, 0, draggedItem);
            }
            this.draggedItemIndex = null;

            const sortedPackingRules = this.packingRules.map((rule, index) => {
                return {
                    id: rule.id,
                    order: index + 1
                }
            });

            this.isSaving = true;
            this.isSorting = true;
            this.$http
                .post(`/api/packing-rules/sort`, sortedPackingRules)
                .catch(err => {
                    EventBus.$emit('show-error', err)
                }).finally(() => {
                    this.isSaving = false;
                    this.isSorting = false;
                });
        },

        switchPackingRule(index) {
            const switchedPackingRule = this.packingRules[index];
            this.currentSwitchingIndex = index;
            this.isSaving = true;
            this.$http
                .post(`/api/packing-rules/${switchedPackingRule.id}`, switchedPackingRule)
                .catch(err => {
                    EventBus.$emit('show-error', err)
                }).finally(() => {
                    this.isSaving = false;
                });
        },

        confirmDelete(index) {
            this.deleteIndex = index;
            this.isDeleteDialogOpen = true;
        },
        deletePackingRule() {
            this.isSaving = true;

            const packingRuleIds = [];

            if(Array.isArray(this.deleteIndex)) {
                this.deleteIndex.forEach(i => {
                    packingRuleIds.push(this.packingRules[i].id);
                });
            } else {
                packingRuleIds.push(this.packingRules[this.deleteIndex].id);
            }

            const jointIds = packingRuleIds.join('-');

            const client_id = this.shopifyUserId ?? '';

            const params = {}
            if(client_id != '') {
                params.client_id = client_id;
            }

            this.$http
                .delete(`/api/packing-rules/${jointIds}`, { params })
                .then(({ data }) => {
                    EventBus.$emit('show-success', 'Successfully deleted packing rule.')

                    if(Array.isArray(this.deleteIndex)) {
                        this.packingRules = this.packingRules.filter((_, i) => !this.deleteIndex.includes(i));
                    } else {
                        this.packingRules.splice(this.deleteIndex, 1);
                    }
                })
                .catch(err => {
                    EventBus.$emit('show-error', err)
                })
                .finally(() => {
                    this.isSaving = false;
                    this.isDeleteDialogOpen = false;
                    this.deleteIndex = null;
                });
        },
        confirmCopy(indexes) {
            this.copyPackingRuleIds = [];
            indexes.forEach(i => {
                this.copyPackingRuleIds.push(this.packingRules[i].id);
            });
            this.isCopyDialogOpen = true;
        },
        copyPackingRules() {
            this.isSaving = true;

            const client_id = this.shopifyUserId ?? '';
            const integrationId = this.copyDestination

            const data = {
                packing_rule_ids: this.copyPackingRuleIds
            };

            const params = {}
            if(client_id != '') {
                params.client_id = client_id;
            }


            this.$http
                .post(`/api/packing-rules/copy/${integrationId}`, data, { params })
                .then(({ data }) => {
                    EventBus.$emit('show-success', 'Successfully copied packing rules.')
                    this.isCopyDialogOpen = false;
                })
                .catch(err => {
                    EventBus.$emit('show-error', err)
                })
                .finally(() => {
                    this.isSaving = false;
                });
        },
        duplicatePackingRules(indexes) {
            const packingRuleIds = indexes.map(i => this.packingRules[i].id);
            this.isDuplicating = true;

            const client_id = this.shopifyUserId ?? '';
            const params = {};
            if (client_id != '') {
                params.client_id = client_id;
            }

            this.$http
                .post(`/api/packing-rules/copy/${this.id}`, { packing_rule_ids: packingRuleIds }, { params })
                .then(({ data }) => {
                    EventBus.$emit('show-success', `Packing rule${packingRuleIds.length > 1 ? 's' : ''} duplicated successfully.`);
                    // Refresh the list
                    this.$http
                        .get(`/api/packing-rules/${this.id}`)
                        .then(({ data }) => {
                            this.packingRules = data.data ?? [];
                        });
                })
                .catch(err => {
                    EventBus.$emit('show-error', err);
                })
                .finally(() => {
                    this.isDuplicating = false;
                    this.selectedPackingRules = [];
                });
        },
        openPackingRuleForm(index, isEdit) {
            this.selectedPackingRuleIndex = index;
            this.formIsOpen = true;
            this.isEdit = isEdit;
        },

        shareBoxSettings() {

            this.shareBtnText = "Copied to clipboard";

            if (navigator.clipboard && navigator.clipboard.writeText) {

                let copiedTxt = "";

                this.packingRules.forEach(rule => {

                    if(rule.enabled==0) {
                        return;
                    }

                    copiedTxt += `Packing Rule: ${rule.label}\n` +
                        `Method: ${rule.packing_method}\n`;

                    // append rules
                    if(rule.rule_conditions && rule.rule_conditions.length > 0) {

                        let rulesSettings = "";
                        rule.rule_conditions.forEach((rules, index) => {
                            let conditions = [];
                            rules.forEach(rule => {
                                let fieldName = rule.fieldName
                                if(fieldName == 'order-meta' || fieldName == 'product-meta') {
                                    fieldName = rule.metaFieldName
                                }
                                conditions.push(`${fieldName} ${rule.operator} "${rule.value}"`);
                            });
                            rulesSettings += index > 0 ? `  AND ` : "  IF ";
                            rulesSettings += `${conditions.join(" OR ")}\n`;
                        });
                        if(rulesSettings != "") {
                            copiedTxt += `Rules:\n` +
                            `${rulesSettings}`;
                        }
                    }

                    let packingRuleOptions = rule.options || {};

                    if (packingRuleOptions.boxSelectionMethod) {
                        copiedTxt += `Box Selection Method: `;
                        if(packingRuleOptions.boxSelectionMethod == "largest-item-fit") {
                            copiedTxt += `Allocate Smallest Package To Largest Item - tends to reduce dead space\n`;
                        } else {
                            copiedTxt += `Allocates Smallest Package That Fits All Items - tends to reduce number of final packages\n`;
                        }
                    }

                    if (packingRuleOptions.overSizedItemPackingMethod) {

                        copiedTxt += `How are oversized items treated: `;
                        if(packingRuleOptions.overSizedItemPackingMethod == "pack-individually") {
                            copiedTxt += `Pack individually\n`;
                        } else {
                            copiedTxt += `Largest Package By Itself\n`;
                        }
                    }

                    if(PACKING_METHODS_WITH_BOXES.includes(rule.packing_method)) {
                        // append box settings
                        let boxSettings = ""
                        rule.packages.forEach(element => {
                            let dims = ` - ${element.length || 0}x${element.width || 0}x${element.height || 0}cm`;
                            boxSettings += `  ${element.sku}${dims} - ${element.weight_limit || 0}kg x ${element.quantity || 1} - ${element.weight || 0}kg\n`;
                        });


                        if(boxSettings != "") {
                            copiedTxt += `Box Settings:\n` +
                            `  SKU - Dimensions - Weight Limit x Qty - Box Weight\n` +
                            `${boxSettings}`;
                        }
                    }

                    if (packingRuleOptions.containerTypes) {
                        copiedTxt += `Item Types: ${packingRuleOptions.containerTypes}`;
                    }

                    if (packingRuleOptions.dynamicLength) {
                        copiedTxt += `Adjust length to accommodate the largest item size: ${packingRuleOptions.dynamicLength}\n`;
                    }

                    copiedTxt += `\n`;
                });

                const content = copiedTxt.replace(/\t/g, '');
                navigator.clipboard.writeText(content)
                    .catch((err) => {
                        console.error('Unable to copy text to clipboard', err);
                        EventBus.$emit('show-error', err);
                    });
            } else {
                EventBus.$emit('show-error', 'Clipboard API not supported in this environment.');
            }

            setTimeout(() => {
                this.shareBtnText = this.originalShareBtnText;
            }, 2000); // reset button text after 2 seconds
        },
        getPackingMethodByKey($key) {
            const packingMethod = this.packingMethods.find(option => option.value == $key);
            return packingMethod ? packingMethod.text : '';
        },
        toggleAll() {
            if (this.selectAll) {
                this.selectedPackingRules = this.packingRules.map(function(_, index) {
                    return index;
                });
            } else {
                this.selectedPackingRules = [];
            }
        },
    }
}
</script>
<style scoped>
.disabled-table {
    pointer-events: none; /* Prevents interaction */
    opacity: 0.6; /* Makes it look disabled */
}
.drag-handle {
    cursor: move;
}
.no-data {
    text-align: center;
    font-size: 1.2rem;
    color: #888;
    margin: 20px 10px;
    display: block;
}
.switch {
    margin: 0;
    padding: 0;
}

</style>